/**
 * Results Collector (pipeline Stage 6)
 *
 * Aggregates simulation evidence emitted by Forma-native analyses and external
 * engines into a single normalized, de-duplicated, canonically-ordered result
 * set plus a screening summary. The collector performs only technical
 * screening aggregation; it never evaluates Israel municipal compliance and
 * never asserts approval, certification, or "municipal-ready" status.
 *
 * The resulting `studies` are ready to hand to the report-agent stage
 * (`buildReportAgentPackage`). The `summary` is a derived convenience for
 * report headers and pipeline dashboards.
 */

import { parseSimulationEvidence, type SimulationEvidence } from './contracts';

type Confidence = SimulationEvidence['confidence'];

/**
 * Canonical reporting order for known studies. Mirrors the architecture's
 * study grouping (sun/solar/daylight, then comfort, then energy/carbon/other).
 * Studies absent from this list are appended after the known studies,
 * preserving their first-seen order.
 */
const STUDY_ORDER: readonly string[] = [
  'sun-hours',
  'shadow-study',
  'solar-rights',
  'daylight-vsc',
  'solar-pv-roof',
  'glare-nespach-6',
  'wind-comfort',
  'noise',
  'thermal-envelope-si-1045',
  'embodied-carbon',
  'green-roof-readiness',
  'microclimate-utci',
];

const CONFIDENCE_RANK: Record<Confidence, number> = { low: 0, medium: 1, high: 2 };
const RANK_TO_CONFIDENCE: readonly Confidence[] = ['low', 'medium', 'high'];

/** A single blocking finding, attributed to the study that raised it. */
export interface CollectedBlocker {
  studyId: string;
  code: string;
  message: string;
}

/** Aggregate screening summary derived from the collected studies. */
export interface ResultsSummary {
  total: number;
  screeningReady: number;
  needsReview: number;
  blocked: number;
  /** Lowest confidence across all collected studies; `low` when empty. */
  overallConfidence: Confidence;
  /** Union of every study's `missingInputs`, de-duplicated and sorted. */
  missingInputs: readonly string[];
  /** Every `blocker`-severity finding, in canonical study order. */
  blockers: readonly CollectedBlocker[];
}

/** Normalized output of the results collector. */
export interface CollectedStudyResults {
  studies: readonly SimulationEvidence[];
  summary: ResultsSummary;
}

export interface CollectStudyResultsInput {
  /**
   * Raw evidence from any source (Forma-native screening, engine fixtures, or
   * deserialized JSON). Each entry is validated with `parseSimulationEvidence`,
   * so malformed evidence fails fast with a descriptive error.
   */
  evidence: readonly unknown[];
}

function studyOrderIndex(studyId: string): number {
  const index = STUDY_ORDER.indexOf(studyId);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/**
 * Collect and normalize simulation evidence into a single screening result set.
 *
 * - Validates every entry via `parseSimulationEvidence`.
 * - De-duplicates by `studyId`, keeping the last occurrence (a re-run
 *   supersedes earlier evidence for the same study).
 * - Orders studies by {@link STUDY_ORDER}; unknown studies follow in
 *   first-seen order.
 * - Derives a status tally, the lowest confidence, the de-duplicated union of
 *   missing inputs, and every blocker-severity finding.
 */
export function collectStudyResults(input: CollectStudyResultsInput): CollectedStudyResults {
  const validated = input.evidence.map((entry) => parseSimulationEvidence(entry));

  // De-duplicate by studyId (last wins) while remembering first-seen order for
  // studies outside the canonical list.
  const byStudyId = new Map<string, SimulationEvidence>();
  const firstSeen = new Map<string, number>();
  validated.forEach((evidence, index) => {
    if (!firstSeen.has(evidence.studyId)) {
      firstSeen.set(evidence.studyId, index);
    }
    byStudyId.set(evidence.studyId, evidence);
  });

  const studies = [...byStudyId.values()].sort((a, b) => {
    const orderDelta = studyOrderIndex(a.studyId) - studyOrderIndex(b.studyId);
    if (orderDelta !== 0) {
      return orderDelta;
    }
    // Stable tie-break for unknown studies: first-seen order.
    return (firstSeen.get(a.studyId) ?? 0) - (firstSeen.get(b.studyId) ?? 0);
  });

  return { studies, summary: summarize(studies) };
}

function summarize(studies: readonly SimulationEvidence[]): ResultsSummary {
  const tally = { screeningReady: 0, needsReview: 0, blocked: 0 };
  const missingInputs = new Set<string>();
  const blockers: CollectedBlocker[] = [];
  let lowestRank = Number.POSITIVE_INFINITY;

  studies.forEach((study) => {
    if (study.status === 'screening-ready') {
      tally.screeningReady += 1;
    } else if (study.status === 'needs-review') {
      tally.needsReview += 1;
    } else {
      tally.blocked += 1;
    }

    lowestRank = Math.min(lowestRank, CONFIDENCE_RANK[study.confidence]);
    study.missingInputs.forEach((item) => missingInputs.add(item));
    study.findings
      .filter((finding) => finding.severity === 'blocker')
      .forEach((finding) =>
        blockers.push({ studyId: study.studyId, code: finding.code, message: finding.message }),
      );
  });

  const overallConfidence: Confidence =
    studies.length === 0 ? 'low' : (RANK_TO_CONFIDENCE[lowestRank] ?? 'low');

  return {
    total: studies.length,
    screeningReady: tally.screeningReady,
    needsReview: tally.needsReview,
    blocked: tally.blocked,
    overallConfidence,
    missingInputs: [...missingInputs].sort(),
    blockers,
  };
}
